type SlotAppointment = {
  estimated_at?: string | null;
  estimated_duration?: number | null;
};

export type AvailabilityBlock = {
  date?: string;
  doctor_start_time?: string;
  doctor_end_time?: string;
  doctor_dsb_slot_length?: number;
  doctor_dsb_full?: boolean;
  appointments?: SlotAppointment[] | null;
};

const toMinutes = (value: string | undefined): number | null => {
  if (!value || !/^\d{2}:\d{2}(?::\d{2})?$/.test(value)) return null;
  const [hours, minutes, seconds = 0] = value.split(":").map(Number);
  return hours < 24 && minutes < 60 && seconds === 0 ? hours * 60 + minutes : null;
};

type AvailabilityContext = {
  start: number;
  end: number;
  length: number;
  bookings: Array<{ start: number; end: number }>;
  date: string;
  today: string;
  currentMinutes: number;
};

const getContext = (block: AvailabilityBlock, now: Date): AvailabilityContext | null => {
  const start = toMinutes(block.doctor_start_time);
  const end = toMinutes(block.doctor_end_time);
  const length = block.doctor_dsb_slot_length;
  if (
    !block.date || !/^\d{4}-\d{2}-\d{2}$/.test(block.date) ||
    start === null || end === null || end < start ||
    typeof length !== "number" || !Number.isInteger(length) || length <= 0 ||
    !Array.isArray(block.appointments)
  ) return null;

  const bookings: Array<{ start: number; end: number }> = [];
  for (const appointment of block.appointments) {
    const dateTime = appointment.estimated_at;
    if (!dateTime || !/^\d{4}-\d{2}-\d{2}/.test(dateTime)) return null;
    if (dateTime.slice(0, 10) !== block.date) continue;
    const time = toMinutes(dateTime.slice(11, 19));
    if (time === null) return null;
    const duration = appointment.estimated_duration;
    const bookingLength = typeof duration === "number" && Number.isFinite(duration) && duration > 0
      ? duration
      : length;
    bookings.push({ start: time, end: time + bookingLength });
  }

  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
  return {
    start, end, length, bookings, date: block.date, today,
    currentMinutes: now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60,
  };
};

const isFree = (context: AvailabilityContext, time: number): boolean =>
  context.date >= context.today &&
  (context.date !== context.today || time >= context.currentMinutes) &&
  time >= context.start && time + context.length <= context.end &&
  !context.bookings.some((booking) => booking.start < time + context.length && booking.end > time);

export const isAppointmentTimeAvailable = (
  block: AvailabilityBlock, time: string | undefined, now = new Date(),
): boolean => {
  if (block.doctor_dsb_full || !time || !/^\d{2}:\d{2}$/.test(time)) return false;
  const context = getContext(block, now);
  const minutes = toMinutes(time);
  return context !== null && minutes !== null && isFree(context, minutes);
};

export const getAvailableSlots = (block: AvailabilityBlock, now = new Date()): string[] | null => {
  if (block.doctor_dsb_full) return [];
  const context = getContext(block, now);
  if (!context) return null;

  const slots: string[] = [];
  for (let time = context.start; time + context.length <= context.end; time += context.length) {
    if (isFree(context, time)) {
      slots.push(`${String(Math.floor(time / 60)).padStart(2, "0")}:${String(time % 60).padStart(2, "0")}`);
    }
  }
  return slots;
};

export const getAvailableSlotCount = (block: AvailabilityBlock, now = new Date()): number | null => {
  const slots = getAvailableSlots(block, now);
  return slots === null ? null : slots.length;
};
